import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { PartialCategory } from "@/types/category.ts";
import { getHostname } from "@/util/hostnames.ts";
import { useCategoriesStore } from "@/stores/categories/index.ts";

export default function useCategoriesStructuredData() {
  const store = useStore();
  const route = useRoute();
  const categoriesStore = useCategoriesStore();

  function generateScript() {
    const isSubcategoriesPage = route.name === "SubCategories";
    const hostname = getHostname();
    const siteId = store.getters.getStyleID;
    const homeUrl = `https://${hostname}/home/${siteId}`;
    const categoriesUrl = `https://${hostname}/categories/${siteId}`;

    // --- Categories Page Schema ---
    const categoriesBreadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: homeUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Top-Level Categories",
          item: categoriesUrl,
        },
      ],
    };

    const categoriesCollectionPages = (categoriesStore.get()?.categories || [])
      .filter((cat: PartialCategory) => cat.isLive)
      .map((cat) => {
        const hasSubcategories =
          Array.isArray(cat.categories) && cat.categories.length > 0;
        const urlType = hasSubcategories ? "subcategories" : "category";
        return {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: cat.categoryName,
          url: `https://${hostname}/${urlType}/${siteId}/${cat._id}`,
        };
      });

    // --- Subcategories Page Schema ---
    const { categoryID } = route.params as { categoryID?: string };
    const parentCategory = categoryID
      ? store.getters["api/category"](categoryID)
      : null;
    const subcategories =
      parentCategory && parentCategory.categories
        ? parentCategory.categories.filter(
            (cat: PartialCategory) => cat && cat.isLive
          )
        : [];

    const subcategoriesBreadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `https://${hostname}/home/${siteId}`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Categories",
          item: `https://${hostname}/categories/${siteId}`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: parentCategory?.categoryName || "",
          item: `https://${hostname}/subcategories/${siteId}/${categoryID}`,
        },
      ],
    };

    const subcategoriesCollectionPages = [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: parentCategory?.categoryName || "",
        url: `https://${hostname}/subcategories/${siteId}/${categoryID}`,
      },
      ...subcategories.map((cat: PartialCategory) => ({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: cat.categoryName,
        url: `https://${hostname}/subcategories/${siteId}/${cat._id}`,
      })),
    ];

    if (isSubcategoriesPage) {
      return [subcategoriesBreadcrumb, ...subcategoriesCollectionPages];
    }
    return [categoriesBreadcrumb, ...categoriesCollectionPages];
  }

  return { generateScript };
}
